import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import ReportsApi from '../../ReportsContainer/reports-api';

// ── Colores por estado ──────────────────────────────────────────────────────
const STATUS_COLORS = {
  Agendada: '#E07B2F',
  Finalizada: '#F5B942',
  Cancelada: '#F9E08A',
  Abandonada: '#777777',
  'No usada': '#BBBBBB',
};

const PILLAR_COLORS = [
  '#5C4033', '#9E9E9E', '#E07B2F', '#F9E08A', '#C2185B',
  '#1976D2', '#388E3C', '#7B1FA2', '#F57C00', '#0097A7',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3, CURRENT_YEAR - 4];

const STATUS_KEYS = ['Agendada', 'Finalizada', 'Cancelada', 'Abandonada', 'No usada'];

// ── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, small }) => (
  <div style={{
    background: '#fff',
    borderRadius: 8,
    padding: small ? '12px 18px' : '16px 22px',
    flex: 1,
    minWidth: small ? 110 : 130,
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
    border: '1px solid #e8e8e8',
  }}>
    <div style={{ fontSize: 12, color: '#888', marginBottom: 4, fontWeight: 400 }}>{label}</div>
    <div style={{ fontSize: small ? 24 : 32, fontWeight: 700, color: '#222' }}>
      {value !== null && value !== undefined
        ? (typeof value === 'number' ? value.toLocaleString('es-CL') : value)
        : 'No hay datos'}
    </div>
  </div>
);

// ── Hero KPI ────────────────────────────────────────────────────────────────
const HeroKpi = ({ label, value }) => (
  <div style={{
    background: '#fff',
    borderRadius: 8,
    padding: '20px 28px',
    minWidth: 200,
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
    border: '1px solid #e8e8e8',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 13, color: '#666', marginBottom: 6, fontWeight: 400 }}>{label}</div>
    <div style={{ fontSize: 40, fontWeight: 700, color: '#222' }}>
      {value !== null && value !== undefined ? value.toLocaleString('es-CL') : '—'}
    </div>
  </div>
);

// ── Custom Tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, normalized }) => {
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8e8e8',
      borderRadius: 6,
      padding: '10px 14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      fontSize: 12,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.fill || p.color }} />
          <span style={{ color: '#555' }}>{p.name}:</span>
          <span style={{ fontWeight: 600, color: '#222' }}>
            {normalized
              ? `${total > 0 ? (((p.value || 0) / total) * 100).toFixed(1) : '0.0'}%`
              : (p.value || 0).toLocaleString('es-CL')}
          </span>
        </div>
      ))}
      {!normalized && payload.length > 1 && (
        <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 6, color: '#888', fontSize: 11 }}>
          Total: <strong style={{ color: '#222' }}>{total.toLocaleString('es-CL')}</strong>
        </div>
      )}
    </div>
  );
};

// ── Toggle button group ─────────────────────────────────────────────────────
const ToggleGroup = ({ options, value, onChange, activeColor }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        style={{
          padding: '4px 12px',
          borderRadius: 16,
          border: 'none',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          background: value === opt.value ? (activeColor || '#E07B2F') : '#f0f0f0',
          color: value === opt.value ? '#fff' : '#555',
          transition: 'all 0.2s',
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────
const SessionsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [viewMode, setViewMode] = useState('mensual');   // 'semanal' | 'mensual'
  const [barMode, setBarMode] = useState('absoluto');    // 'absoluto' | 'porcentual'
  const [tab, setTab] = useState('estado');              // 'estado' | 'pilar'

  useEffect(() => {
    setLoading(true);
    setError(null);
    ReportsApi.getSessionsDashboard(selectedYear)
      .then((res) => setData(res.data))
      .catch(() => setError('No se pudo cargar el dashboard. Intenta nuevamente.'))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260, color: '#888', fontSize: 14 }}>
      Cargando estadísticas...
    </div>
  );

  if (error) return (
    <div style={{ color: '#c0392b', padding: 20, background: '#fff5f5', borderRadius: 8 }}>
      {error}
    </div>
  );

  if (!data) return null;

  const chartData = viewMode === 'semanal' ? data.semanal : data.mensual;
  const xKey = viewMode === 'semanal' ? 'semana' : 'mes';
  const pilarChartData = viewMode === 'semanal' ? data.semanal_pilares : data.mensual_pilares;
  const pilarXKey = viewMode === 'semanal' ? 'semana' : 'mes';
  const isPortcentual = barMode === 'porcentual';

  return (
    <div style={{ background: '#EEEEE8', padding: '24px', borderRadius: 12 }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20,
      }}>
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>
          {tab === 'pilar' ? `Mentorías por pilares ${selectedYear}` : `Mentorías ${selectedYear}`}
        </h2>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Tabs */}
          <ToggleGroup
            options={[
              { value: 'estado', label: 'Por estado' },
              { value: 'pilar', label: 'Por pilar' },
            ]}
            value={tab}
            onChange={setTab}
            activeColor="#4C7A6B"
          />

          <div style={{ width: 1, background: '#ccc', height: 20, margin: '0 4px' }} />

          {/* Year */}
          <div style={{ display: 'flex', gap: 4 }}>
            {YEARS.map((y) => (
              <button key={y} onClick={() => setSelectedYear(y)} style={{
                padding: '5px 14px',
                borderRadius: 20,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: selectedYear === y ? '#E07B2F' : '#fff',
                color: selectedYear === y ? '#fff' : '#555',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
              }}>
                {y}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── KPIs: estado ── */}
      {tab === 'estado' && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <HeroKpi label="Total finalizadas" value={data.totalFinalizadas} />
          <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
            <KpiCard label="Finalizadas" value={data.kpis.finalizadas} small />
            <KpiCard label="Canceladas" value={data.kpis.canceladas} small />
            <KpiCard label="Abandonadas" value={data.kpis.abandonadas} small />
            <KpiCard label="No usadas" value={data.kpis.noUsadas} small />
            <KpiCard label="Agendadas" value={data.kpis.agendadas} small />
            <KpiCard label="Totales" value={data.kpis.total} small />
          </div>
        </div>
      )}

      {/* ── KPIs: pilar ── */}
      {tab === 'pilar' && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {data.pilares.slice(0, 6).map((p, idx) => (
            <KpiCard
              key={p.pilar}
              label={p.pilar}
              value={p.total > 0 ? p.total : null}
              small
            />
          ))}
        </div>
      )}

      {/* ── Chart header ── */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: '16px 20px',
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        border: '1px solid #e8e8e8',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#333' }}>
            Mentorías {viewMode === 'semanal' ? 'semanales' : 'mensuales'}
          </h3>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <ToggleGroup
              options={[
                { value: 'semanal', label: 'Semanal' },
                { value: 'mensual', label: 'Mensual' },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
            {tab === 'estado' && (
              <>
                <div style={{ width: 1, background: '#e0e0e0', margin: '0 2px', height: 18 }} />
                <ToggleGroup
                  options={[
                    { value: 'absoluto', label: '#' },
                    { value: 'porcentual', label: '%' },
                  ]}
                  value={barMode}
                  onChange={setBarMode}
                  activeColor="#4C7A6B"
                />
              </>
            )}
          </div>
        </div>

        {/* ── Chart: por estado ── */}
        {tab === 'estado' && (
          chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 16, left: 0, bottom: 24 }}
                barCategoryGap="30%"
                stackOffset={isPortcentual ? 'expand' : 'none'}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey={xKey}
                  tick={{ fontSize: 10, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={isPortcentual ? (v) => `${(v * 100).toFixed(0)}%` : undefined}
                />
                <Tooltip content={<CustomTooltip normalized={isPortcentual} />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="Agendada"  stackId="a" fill={STATUS_COLORS.Agendada}  />
                <Bar dataKey="Finalizada" stackId="a" fill={STATUS_COLORS.Finalizada} />
                <Bar dataKey="Cancelada" stackId="a" fill={STATUS_COLORS.Cancelada} />
                <Bar dataKey="Abandonada" stackId="a" fill={STATUS_COLORS.Abandonada} />
                <Bar dataKey="No usada"  stackId="a" fill={STATUS_COLORS['No usada']} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Sin datos para este período</div>
          )
        )}

        {/* ── Chart: por pilar ── */}
        {tab === 'pilar' && (
          pilarChartData && pilarChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={pilarChartData}
                margin={{ top: 4, right: 16, left: 0, bottom: 24 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey={pilarXKey}
                  tick={{ fontSize: 10, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {(data.pilaresUnicos || []).map((pilar, idx) => (
                  <Bar
                    key={pilar}
                    dataKey={pilar}
                    stackId="a"
                    fill={PILLAR_COLORS[idx % PILLAR_COLORS.length]}
                    radius={idx === (data.pilaresUnicos.length - 1) ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Sin datos para este período</div>
          )
        )}
      </div>
    </div>
  );
};

export default SessionsDashboard;
